/**
 * Speaking content type schema — Page intro + record-audio prompts
 */

import type {
    CompositeQuestionsLayoutFields,
    ContentConfig,
    LayoutConfig,
    ScoringConfig,
} from '@ai-content-hub/content-core';
import type { RecordAudioElementData } from '@ai-content-hub/content-elements';
import type { PageElementData } from '@ai-content-hub/content-elements';
import { defaultPageData } from '@ai-content-hub/content-elements';

export type { PageElementData } from '@ai-content-hub/content-elements';

export type SpeakingItem = {
    id: string;
    type: 'record-audio';
    data: RecordAudioElementData;
    points?: number;
};

export interface SpeakingData extends CompositeQuestionsLayoutFields {
    title?: string;
    description?: string;
    /** Learner-facing rationale displayed after submission/review. */
    explanation?: string;
    pageContent: PageElementData;
    items: SpeakingItem[];
}

export type SpeakingSplitRatio = '50-50' | '60-40' | '40-60' | '70-30' | '30-70';

export interface SpeakingLayoutConfig extends LayoutConfig {
    /** When type is horizontal: lesson column vs prompts column */
    splitRatio?: SpeakingSplitRatio;
    /** Show all prompts at once (`all`) or one at a time with tab bar (`tabs`). Legacy `step` is treated as `tabs` in the player. */
    displayMode?: 'all' | 'step' | 'tabs';
}

export interface SpeakingContent {
    id: string;
    type: 'speaking';
    version: string;
    data: SpeakingData;
    config?: SpeakingConfig;
}

/**
 * EAA-1857: the QT_SPEAK_* grading types `/api/v3/scoring/speaking` accepts.
 *
 * `gradingQuestionType` existed in config but had no editor control, so authors
 * could not pick how a prompt is graded — an imported Moodle qtype_speechace
 * question was stuck on whatever the import wrote. Mirrors Essay's
 * `GRADING_QUESTION_TYPES`; `scorer.ts` validates against these same codes.
 *
 * EAA-1890: each entry carries an `exampleKey` into `defaultSpeakingI18n`. The task
 * names are PTE-style labels — "Retell Lecture", "Respond to a Situation" — which say
 * nothing about what the learner is asked to do, so authors picked by guesswork. The
 * example is a translated i18n key, not a literal here, because the names stay in
 * English (proper nouns of the scoring API) while the prose must not.
 *
 * `exampleKey` is the one-liner shown in the dropdown; `examplePromptKey` and
 * `exampleAnswerKey` feed the "view example" dialog, which shows the request this
 * type posts to `/api/v3/scoring/speaking` and the graded result that comes back.
 */
export const SPEAKING_GRADING_QUESTION_TYPES = [
    {
        code: 'QT_SPEAK_READ_ALOUD' as const,
        name: 'Read Aloud',
        exampleKey: 'speakingGradingExampleReadAloud',
        examplePromptKey: 'speakingGradingPromptReadAloud',
        exampleAnswerKey: 'speakingGradingAnswerReadAloud',
    },
    {
        code: 'QT_SPEAK_REPEAT_SENTENCE' as const,
        name: 'Repeat Sentence',
        exampleKey: 'speakingGradingExampleRepeatSentence',
        examplePromptKey: 'speakingGradingPromptRepeatSentence',
        exampleAnswerKey: 'speakingGradingAnswerRepeatSentence',
    },
    {
        code: 'QT_SPEAK_SUMMARIZE_GROUP_DISCUSSION' as const,
        name: 'Summarize Group Discussion',
        exampleKey: 'speakingGradingExampleSummarizeGroupDiscussion',
        examplePromptKey: 'speakingGradingPromptSummarizeGroupDiscussion',
        exampleAnswerKey: 'speakingGradingAnswerSummarizeGroupDiscussion',
    },
    {
        code: 'QT_SPEAK_ANSWER_SHORT_QUESTION' as const,
        name: 'Answer Short Question',
        exampleKey: 'speakingGradingExampleAnswerShortQuestion',
        examplePromptKey: 'speakingGradingPromptAnswerShortQuestion',
        exampleAnswerKey: 'speakingGradingAnswerAnswerShortQuestion',
    },
    {
        code: 'QT_SPEAK_RETELL_LECTURE' as const,
        name: 'Retell Lecture',
        exampleKey: 'speakingGradingExampleRetellLecture',
        examplePromptKey: 'speakingGradingPromptRetellLecture',
        exampleAnswerKey: 'speakingGradingAnswerRetellLecture',
    },
    {
        code: 'QT_SPEAK_PIC_FREE' as const,
        name: 'Describe Image',
        exampleKey: 'speakingGradingExampleDescribeImage',
        examplePromptKey: 'speakingGradingPromptDescribeImage',
        exampleAnswerKey: 'speakingGradingAnswerDescribeImage',
    },
    {
        code: 'QT_SPEAK_RESPOND_SITUATION' as const,
        name: 'Respond to a Situation',
        exampleKey: 'speakingGradingExampleRespondSituation',
        examplePromptKey: 'speakingGradingPromptRespondSituation',
        exampleAnswerKey: 'speakingGradingAnswerRespondSituation',
    },
] as const;

/**
 * Types whose grading request must carry the page picture as multipart, matching the
 * agent curl for `QT_SPEAK_PIC_FREE`. Lives here (not in `scorer.ts`) so the
 * "view example" dialog and the grader describe the same request shape.
 */
export const SPEAKING_QUESTION_TYPES_WITH_PICTURE = ['QT_SPEAK_PIC_FREE'] as const;

/** Per-word SpeechAce scoring against a known script; only used when explicitly chosen. */
export const SPEAKING_READ_ALOUD_QUESTION_TYPE = 'QT_SPEAK_READ_ALOUD';

/**
 * ELMS-2369: applied when `gradingQuestionType` is unset. Speaking prompts are free-talk by
 * default (a bulleted brief, no script to read), so the default is the open-ended criteria type,
 * not Read Aloud (which would score the brief as if it were a script).
 */
export const DEFAULT_SPEAKING_GRADING_QUESTION_TYPE = 'QT_SPEAK_RESPOND_SITUATION';

export interface SpeakingScoringConfig extends ScoringConfig {
    pointsPerQuestion?: number;
    passingThreshold?: number;
    /**
     * When true, submit calls `contentService.gradeSpeaking` once per recorded prompt
     * and sets score to the average of API overall percentages (max 100).
     */
    aiGradingEnabled?: boolean;
    /**
     * QT_SPEAK_* for `/api/v3/scoring/speaking`. Unset ⇒ `QT_SPEAK_RESPOND_SITUATION` (ELMS-2369 free-talk default;
     * audio + question only). AI describe-image content should use `QT_SPEAK_PIC_FREE`
     * (set on save from AI job or in editor).
     *
     * EAA-1890: `QT_SPEAK_READ_ALOUD` is the exception — it does NOT go to that endpoint.
     * It is graded per word by SpeechAce through `speakingScoreService.score`, the same
     * call interactive-reading makes, and rendered by `SpeakingPronunciationPanel`.
     */
    gradingQuestionType?: string;
    /**
     * Learner id for multipart grading (`user_id`). Prefer `ContentPlayer` `userId` or embed
     * `?userId=` so saved config does not store PII; this field is an optional fallback.
     */
    userId?: string;
}

export interface SpeakingConfig extends Omit<ContentConfig, 'layout' | 'scoring'> {
    layout?: SpeakingLayoutConfig;
    scoring?: SpeakingScoringConfig;
}

export const defaultSpeakingI18n = {
    en: {
        submit: 'Submit',
        submitAnswers: 'Submit',
        retry: 'Try Again',
        question: 'Prompt',
        of: 'of',
        progress: 'Progress',
        answered: 'recorded',
        answerAllQuestions: 'Complete all recordings to submit',
        answerAtLeastOneQuestion: 'Record at least one response to submit',
        noQuestions: 'No speaking prompts yet',
        speakingPromptsHeading: 'Speaking prompts',
        congratulations: 'Thank you!',
        keepPracticing: 'Keep practicing',
        next: 'Next',
        previous: 'Previous',
        aiGradingCriteria: 'Criteria',
        aiGradingStrengths: 'Strengths',
        aiGradingWeaknesses: 'Weaknesses',
        aiGradingCorrections: 'Corrections',
        aiGradingSuggestedRevision: 'Suggested revision',
        aiGradingTranscript: 'Transcript',
        aiGradingOverallScore: 'Overall score',
        aiGradingWordCount: 'Word count',
        aiGradingHideDetails: 'Hide details',
        aiGradingMoreDetails: 'More details',
        aiGradingPrompt: 'Speaking prompt',
        aiGradingResults: 'AI speaking results',
        pronunciationResults: 'Pronunciation results',
        pronunciationReference: 'What you were asked to read',
        pronunciationYourScore: 'Your score',
        pronunciationWordScore: 'Score',
        pronunciationLegendGood: 'Clear',
        pronunciationLegendOk: 'Almost',
        pronunciationLegendBad: 'Needs work',
        explanation: 'Explanation',
        contentTab: 'Content',
        questionsTab: 'Questions',
        configurationTab: 'Configuration',
        title: 'Title',
        enterTitle: 'Enter title',
        descriptionOptional: 'Description (Optional)',
        enterDescription: 'Enter description or instructions for learners',
        prompts: 'Prompts',
        noPromptsYet: 'No prompts yet. Add a record-audio task.',
        addSection: 'Add section',
        removeSection: 'Remove section',
        addPrompt: 'Add speaking prompt',
        section: 'Section',
        promptDetail: 'Prompt',
        selectPromptMessage: 'Select a prompt, a section, or add one to start editing.',
        removePrompt: 'Remove prompt',
        multiPromptLegacyNotice:
            'This activity has multiple prompts in saved data; only the first is editable here.',
        preparingPrompt: 'Preparing speaking prompt…',
        speakingGradingType: 'Speaking grading type',
        speakingSelectGradingType: '— Select grading type —',
        speakingAutoGradingHint:
            'Recordings are only marked as submitted. Tick "Automatic scoring" above to have AI score them.',
        speakingGradingExampleReadAloud:
            'e.g. "Read this sentence aloud: The library opens at eight." — scored word by word.',
        speakingGradingExampleRepeatSentence:
            'e.g. learner hears "She moved to Hanoi last year" and repeats it exactly.',
        speakingGradingExampleSummarizeGroupDiscussion:
            'e.g. after a three-person discussion on recycling, summarise what each speaker argued.',
        speakingGradingExampleAnswerShortQuestion:
            'e.g. "What do you call the meal you eat in the morning?" — one or two words.',
        speakingGradingExampleRetellLecture:
            'e.g. listen to a two-minute talk on volcanoes, then retell the main points.',
        speakingGradingExampleDescribeImage:
            'e.g. describe the chart on screen — needs a picture on the lesson page.',
        speakingGradingExampleRespondSituation:
            'e.g. "You arrive late to a meeting. What do you say?" — respond as you would in person.',
        speakingGradingViewExample: 'View example',
        speakingGradingExampleTitle: 'Example',
        speakingGradingExampleInput: 'Sent for grading',
        speakingGradingExampleOutput: 'Returned after grading',
        speakingGradingExampleFieldPrompt: 'Question content',
        speakingGradingExampleFieldAudio: 'Learner recording',
        speakingGradingExampleFieldAudioValue: 'the audio file the learner recorded (webm/wav/mp3)',
        speakingGradingExampleFieldPicture: 'Page picture',
        speakingGradingExampleFieldPictureValue:
            'the first image on the lesson page — this type is not graded without one',
        speakingGradingExampleTranscriptNote:
            'Sample values. Real scores come from the grading API.',
        speakingGradingExampleStrength: 'Clear pronunciation and steady pace.',
        speakingGradingExampleWeakness: 'A few filler pauses before longer clauses.',
        speakingGradingPromptReadAloud: 'Read this sentence aloud: The library opens at eight.',
        speakingGradingAnswerReadAloud: 'The library opens at eight.',
        speakingGradingPromptRepeatSentence:
            'Listen and repeat exactly: She moved to Hanoi last year.',
        speakingGradingAnswerRepeatSentence: 'She moved to Hanoi last year.',
        speakingGradingPromptSummarizeGroupDiscussion:
            'Three students discuss recycling at school. Summarise what each speaker argued.',
        speakingGradingAnswerSummarizeGroupDiscussion:
            'The first speaker wanted separate bins in every classroom. The second said students would not sort waste without training. The third suggested a monthly weigh-in to show progress.',
        speakingGradingPromptAnswerShortQuestion:
            'What do you call the meal you eat in the morning?',
        speakingGradingAnswerAnswerShortQuestion: 'Breakfast.',
        speakingGradingPromptRetellLecture:
            'Listen to the two-minute talk on volcanoes, then retell the main points.',
        speakingGradingAnswerRetellLecture:
            'The talk explained that volcanoes form where plates meet, that magma pressure decides how violent an eruption is, and that monitoring gas output helps predict one.',
        speakingGradingPromptDescribeImage:
            'Describe the chart on the screen in about forty seconds.',
        speakingGradingAnswerDescribeImage:
            'The bar chart compares rainfall across four cities. Hue is the wettest at around two thousand millimetres, while Nha Trang is the driest.',
        speakingGradingPromptRespondSituation: 'You arrive late to a meeting. What do you say?',
        speakingGradingAnswerRespondSituation:
            'I am sorry I am late — the bus was held up. Please carry on, I will catch up from the notes.',
    },
    vi: {
        submit: 'Nộp bài',
        submitAnswers: 'Nộp bài',
        retry: 'Thử lại',
        question: 'Yêu cầu',
        of: 'trên',
        progress: 'Tiến độ',
        answered: 'đã ghi',
        answerAllQuestions: 'Hoàn thành tất cả bản ghi để nộp',
        answerAtLeastOneQuestion: 'Ghi âm ít nhất một câu trả lời để nộp',
        noQuestions: 'Chưa có câu hỏi nói',
        speakingPromptsHeading: 'Phần nói',
        congratulations: 'Cảm ơn bạn!',
        keepPracticing: 'Tiếp tục luyện tập',
        next: 'Tiếp theo',
        previous: 'Trước',
        aiGradingCriteria: 'Tiêu chí đánh giá',
        aiGradingStrengths: 'Điểm mạnh',
        aiGradingWeaknesses: 'Điểm yếu',
        aiGradingCorrections: 'Sửa lỗi',
        aiGradingSuggestedRevision: 'Bản sửa đổi đề xuất',
        aiGradingTranscript: 'Bản dịch',
        aiGradingOverallScore: 'Điểm tổng',
        aiGradingWordCount: 'Số từ',
        aiGradingHideDetails: 'Ẩn chi tiết',
        aiGradingMoreDetails: 'Xem chi tiết',
        aiGradingPrompt: 'Câu hỏi nói',
        aiGradingResults: 'Kết quả chấm điểm AI',
        pronunciationResults: 'Kết quả phát âm',
        pronunciationReference: 'Nội dung cần đọc',
        pronunciationYourScore: 'Điểm của bạn',
        pronunciationWordScore: 'Điểm',
        pronunciationLegendGood: 'Rõ',
        pronunciationLegendOk: 'Tạm được',
        pronunciationLegendBad: 'Cần luyện thêm',
        explanation: 'Giải thích',
        contentTab: 'Nội dung',
        questionsTab: 'Câu hỏi',
        configurationTab: 'Cấu hình',
        title: 'Tiêu đề',
        enterTitle: 'Nhập tiêu đề',
        descriptionOptional: 'Mô tả (Tuỳ chọn)',
        enterDescription: 'Nhập mô tả hoặc hướng dẫn cho học viên',
        prompts: 'Câu hỏi',
        noPromptsYet: 'Chưa có câu hỏi. Thêm bài tập ghi âm.',
        addSection: 'Thêm nhóm',
        removeSection: 'Xóa nhóm',
        addPrompt: 'Thêm câu hỏi nói',
        section: 'Nhóm',
        promptDetail: 'Câu hỏi',
        selectPromptMessage: 'Chọn một câu hỏi, một nhóm, hoặc thêm mới để chỉnh sửa.',
        removePrompt: 'Xóa câu hỏi',
        multiPromptLegacyNotice:
            'Hoạt động này có nhiều câu hỏi trong dữ liệu đã lưu; chỉ câu hỏi đầu tiên có thể chỉnh sửa ở đây.',
        preparingPrompt: 'Đang chuẩn bị câu hỏi nói…',
        speakingGradingType: 'Loại chấm điểm nói',
        speakingSelectGradingType: '— Chọn loại chấm điểm —',
        speakingAutoGradingHint:
            'Bản ghi âm chỉ được ghi nhận là đã nộp. Tick "Tự động chấm điểm" ở trên để AI chấm điểm.',
        speakingGradingExampleReadAloud:
            'VD: "Đọc to câu sau: Thư viện mở cửa lúc tám giờ." — chấm điểm từng từ.',
        speakingGradingExampleRepeatSentence:
            'VD: học viên nghe "Cô ấy chuyển đến Hà Nội năm ngoái" rồi nhắc lại đúng nguyên câu.',
        speakingGradingExampleSummarizeGroupDiscussion:
            'VD: sau cuộc thảo luận ba người về tái chế, tóm tắt lại ý kiến của từng người.',
        speakingGradingExampleAnswerShortQuestion:
            'VD: "Bữa ăn vào buổi sáng gọi là gì?" — trả lời một đến hai từ.',
        speakingGradingExampleRetellLecture:
            'VD: nghe bài nói hai phút về núi lửa, sau đó kể lại các ý chính.',
        speakingGradingExampleDescribeImage:
            'VD: miêu tả biểu đồ trên màn hình — cần có hình ảnh trong trang nội dung.',
        speakingGradingExampleRespondSituation:
            'VD: "Bạn đến họp muộn. Bạn sẽ nói gì?" — trả lời như khi nói trực tiếp.',
        speakingGradingViewExample: 'Xem ví dụ',
        speakingGradingExampleTitle: 'Ví dụ',
        speakingGradingExampleInput: 'Dữ liệu gửi đi để chấm',
        speakingGradingExampleOutput: 'Kết quả trả về sau khi chấm',
        speakingGradingExampleFieldPrompt: 'Nội dung câu hỏi',
        speakingGradingExampleFieldAudio: 'Bản ghi âm của học viên',
        speakingGradingExampleFieldAudioValue: 'tệp âm thanh học viên vừa ghi (webm/wav/mp3)',
        speakingGradingExampleFieldPicture: 'Hình ảnh trong trang nội dung',
        speakingGradingExampleFieldPictureValue:
            'ảnh đầu tiên trong trang nội dung — thiếu ảnh thì loại này không chấm được',
        speakingGradingExampleTranscriptNote:
            'Đây là số liệu mẫu. Điểm thật do API chấm điểm trả về.',
        speakingGradingExampleStrength: 'Phát âm rõ, tốc độ nói đều.',
        speakingGradingExampleWeakness: 'Còn vài chỗ ngập ngừng trước các câu dài.',
        speakingGradingPromptReadAloud: 'Đọc to câu sau: Thư viện mở cửa lúc tám giờ.',
        speakingGradingAnswerReadAloud: 'Thư viện mở cửa lúc tám giờ.',
        speakingGradingPromptRepeatSentence:
            'Nghe và nhắc lại đúng nguyên câu: Cô ấy chuyển đến Hà Nội năm ngoái.',
        speakingGradingAnswerRepeatSentence: 'Cô ấy chuyển đến Hà Nội năm ngoái.',
        speakingGradingPromptSummarizeGroupDiscussion:
            'Ba học sinh thảo luận về việc tái chế ở trường. Hãy tóm tắt ý kiến của từng người.',
        speakingGradingAnswerSummarizeGroupDiscussion:
            'Bạn thứ nhất muốn đặt thùng rác phân loại ở mỗi lớp. Bạn thứ hai cho rằng không hướng dẫn thì học sinh sẽ không phân loại. Bạn thứ ba đề xuất cân rác hằng tháng để thấy kết quả.',
        speakingGradingPromptAnswerShortQuestion: 'Bữa ăn vào buổi sáng gọi là gì?',
        speakingGradingAnswerAnswerShortQuestion: 'Bữa sáng.',
        speakingGradingPromptRetellLecture:
            'Nghe bài nói hai phút về núi lửa, sau đó kể lại các ý chính.',
        speakingGradingAnswerRetellLecture:
            'Bài nói giải thích núi lửa hình thành ở nơi các mảng kiến tạo gặp nhau, áp suất macma quyết định mức độ dữ dội của vụ phun, và theo dõi lượng khí thoát ra giúp dự báo.',
        speakingGradingPromptDescribeImage:
            'Miêu tả biểu đồ trên màn hình trong khoảng bốn mươi giây.',
        speakingGradingAnswerDescribeImage:
            'Biểu đồ cột so sánh lượng mưa của bốn thành phố. Huế mưa nhiều nhất, khoảng hai nghìn milimét, còn Nha Trang ít mưa nhất.',
        speakingGradingPromptRespondSituation: 'Bạn đến họp muộn. Bạn sẽ nói gì?',
        speakingGradingAnswerRespondSituation:
            'Xin lỗi vì tôi đến muộn, xe buýt bị tắc đường. Mọi người cứ tiếp tục, tôi sẽ xem lại phần ghi chú để theo kịp.',
    },
};

export const defaultSpeakingConfig: SpeakingConfig = {
    layout: {
        type: 'horizontal',
        splitRatio: '50-50',
        displayMode: 'all',
    },
    behavior: {
        allowRetry: true,
        shuffleChoices: false,
        showCorrectAnswer: false,
        showExplanation: false,
        examMode: false,
        instantFeedback: false,
    },
    scoring: {
        pointsPerQuestion: 1,
        partialCreditEnabled: true,
        passingThreshold: 0.7,
    },
    i18n: {
        defaultLanguage: 'en',
        languages: defaultSpeakingI18n,
    },
};

export const defaultSpeakingData: SpeakingData = {
    // EAA-2082: a new Speaking starts with an EMPTY title so the editor shows only the
    // placeholder. A non-empty default ("Speaking") was picked up by the question-bank page
    // as the content title and saved although nobody typed it.
    title: '',
    description: '',
    pageContent: { ...defaultPageData },
    items: [],
};

export const speakingSchema = {
    type: 'speaking' as const,
    title: 'Speaking',
    description: 'Lesson content with microphone speaking prompts',
    version: '1.0.0',
    category: 'assessment' as const,
    icon: 'Mic',
    tags: ['speaking', 'assessment', 'audio', 'record-audio'],
    defaultData: defaultSpeakingData,
    defaultConfig: defaultSpeakingConfig,
    fields: [],
};
